import React, { useState } from 'react';
import { Card, CardContent, Typography, IconButton, Menu, MenuItem, Box, Chip } from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { TSubmissionCategoryAward } from '../../../../types/global/helper';

interface AwardCategoryCardProps {
    award: TSubmissionCategoryAward;
    onMoreAction: (action: string, awardId: string) => void;
    hasSubmissions: boolean;
}

const AwardCategoryCard: React.FC<AwardCategoryCardProps> = ({ award, onMoreAction, hasSubmissions }) => {
    const { themeStyle } = useThemeContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action: string) => {
        onMoreAction(action, award._id);
        handleMenuClose();
    };

    return (
        <Card
            sx={{
                backgroundColor: theme[themeStyle].secondaryBg,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].primary_white,
                        }}>
                        {award.category_title}
                    </Typography>
                    {!hasSubmissions && (
                        <IconButton onClick={handleMenuOpen} sx={{ color: theme[themeStyle].primary_white }}>
                            <MoreVertIcon />
                        </IconButton>
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    {!!award.prize && Number(award.prize) > 0 ? (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].primary_white,
                                mb: 1,
                            }}>
                            Prize: ${award.prize}
                        </Typography>
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: theme[themeStyle].primary_white,
                                mb: 1,
                            }}>
                            {award.additional_prize && award.additional_prize.length > 0 ? '' : 'No prize'}
                        </Typography>
                    )}

                    {award.additional_prize && award.additional_prize.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].primary_white,
                                    mb: 0.5,
                                }}>
                                Additional Prizes:
                            </Typography>
                            {award.additional_prize.map((prize: string, index: number) => (
                                <Typography
                                    key={index}
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                        ml: 2,
                                    }}>
                                    • {prize}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    {award.sub_category && (
                        <Chip
                            label={award.sub_category.charAt(0).toUpperCase() + award.sub_category.slice(1)}
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    )}
                </Box>
            </CardContent>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: theme[themeStyle].table_header,
                    },
                }}>
                <MenuItem onClick={() => handleAction('edit')} sx={{ color: theme[themeStyle].primary_white }}>
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleAction('delete')} sx={{ color: theme[themeStyle].primary_white }}>
                    Delete
                </MenuItem>
            </Menu>
        </Card>
    );
};

export default AwardCategoryCard;
